(() => {
  'use strict';
  if (window.SSCartGuides) return;

  const sectionName = 'ss-cart-guides';
  const storageKey = 'ss-cart-guide-opt-outs';

  // This pure planner keeps customer-added guides separate from automatic inclusions.
  function planChanges(state, optOuts = new Set()) {
    const updates = {};
    const additions = [];
    for (const guide of state.guides) {
      const required = state.enabled && state.items.some((item) =>
        !item.automaticGroup && item.quantity > 0 && item.price > 0 &&
        item.requiresShipping && item.tags.includes(guide.tag)
      );
      if (!required) optOuts.delete(String(guide.variantId));
      const matching = state.items.filter((item) => String(item.variantId) === String(guide.variantId));
      const manual = matching.some((item) => !item.automaticGroup && item.quantity > 0);
      let kept = false;
      for (const item of state.items.filter((candidate) => candidate.automaticGroup === guide.group)) {
        if (!required || !guide.available || manual || kept ||
            String(item.variantId) !== String(guide.variantId) || item.price !== 0 || item.requiresShipping) {
          updates[item.key] = 0;
        } else {
          kept = true;
          if (item.quantity !== 1) updates[item.key] = 1;
        }
      }
      if (required && guide.available && !manual && !kept && !optOuts.has(String(guide.variantId))) {
        additions.push({ id: guide.variantId, quantity: 1, properties: { _ss_auto_guide: guide.group } });
      }
    }
    return { updates, additions };
  }

  class CartGuides {
    constructor(state) {
      this.state = state;
      this.queue = Promise.resolve();
      this.pending = 0;
      this.optOuts = new Set();
      this.locked = new Map();
      this.checkoutForms = new WeakSet();
      try {
        this.optOuts = new Set(JSON.parse(sessionStorage.getItem(storageKey) || '[]'));
      } catch { /* Cart behaviour also works when browser storage is unavailable. */ }

      document.addEventListener('submit', (event) => this.onCheckout(event), true);
      document.addEventListener('click', (event) => this.onCheckoutLink(event), true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event.cartData?.ssGuidesSynced && event.source !== 'ss-cart-guides') this.sync();
        });
      }
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.sync();
      });

      const changes = planChanges(state, this.optOuts);
      this.saveOptOuts();
      if (Object.keys(changes.updates).length || changes.additions.length) this.sync();
    }

    saveOptOuts() {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify([...this.optOuts]));
      } catch { /* Storage is optional. */ }
    }

    setBusy(busy) {
      if (busy) {
        document.querySelectorAll('[name="checkout"], shopify-accelerated-checkout-cart, .additional-checkout-buttons')
          .forEach((element) => {
            if (this.locked.has(element)) return;
            this.locked.set(element, { inert: element.inert, disabled: element.disabled });
            element.inert = true;
            if ('disabled' in element) element.disabled = true;
          });
      } else {
        for (const [element, previous] of this.locked) {
          element.inert = previous.inert;
          if ('disabled' in element) element.disabled = previous.disabled;
        }
        this.locked.clear();
      }
    }

    enqueue(work) {
      this.pending += 1;
      this.setBusy(true);
      const task = this.queue.catch(() => {}).then(work);
      const finished = task.finally(() => {
        this.pending -= 1;
        if (!this.pending) this.setBusy(false);
      });
      this.queue = finished.catch(() => {});
      return finished;
    }

    async jsonRequest(url, body) {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
        ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' }),
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) throw new Error('Cart guide request failed');
      return data;
    }

    async readState() {
      const url = new URL(this.state.cartUrl, window.location.origin);
      url.searchParams.set('section_id', sectionName);
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Cart guide state unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const configuration = html.querySelector('[data-ss-cart-guides]');
      if (!configuration) throw new Error('Cart guide state missing');
      this.state = JSON.parse(configuration.textContent);
      return this.state;
    }

    async reconcile(options = {}) {
      if (window.Shopify?.designMode) return null;
      const state = await this.readState();
      if (options.removedVariant && state.guides.some((guide) =>
        String(guide.variantId) === String(options.removedVariant))) {
        this.optOuts.add(String(options.removedVariant));
      }
      const changes = planChanges(state, this.optOuts);
      this.saveOptOuts();
      if (!Object.keys(changes.updates).length && !changes.additions.length) return null;
      const sectionIds = [...new Set(options.sections || [])].slice(0, 5);
      const render = sectionIds.length ? {
        sections: sectionIds,
        sections_url: options.sectionsUrl || window.location.pathname,
      } : {};
      let result;
      if (Object.keys(changes.updates).length) {
        result = await this.jsonRequest(window.routes.cart_update_url, { updates: changes.updates, ...render });
      }
      if (changes.additions.length) {
        result = await this.jsonRequest(window.routes.cart_add_url, { items: changes.additions, ...render });
      }
      const cart = result.token ? result : await this.jsonRequest(`${state.cartUrl}.js`);
      return { ...cart, sections: result.sections, ssGuidesSynced: true };
    }

    reportError() {
      const status = document.getElementById('ss-cart-guides-status');
      if (status) status.textContent = this.state.errorMessage;
    }

    mutate(request, options = {}) {
      return this.enqueue(async () => {
        const result = await request();
        if (result.status || result.errors) return result;
        try {
          const settled = await this.reconcile(options);
          return { ...result, ...(settled || {}), ssGuidesSynced: true };
        } catch {
          // A PDF failure must never retry a successful bag addition or block its checkout.
          this.reportError();
          return { ...result, ssGuidesSynced: true };
        }
      });
    }

    visibleSections() {
      const cartItems = document.querySelector('cart-items, cart-drawer-items');
      const descriptors = cartItems?.getSectionsToRender?.() || [];
      const sections = descriptors.map((descriptor) => descriptor.section || descriptor.id);
      if (document.getElementById('cart-notification-button')) sections.push('cart-notification-button');
      if (document.getElementById('cart-icon-bubble')) sections.push('cart-icon-bubble');
      const upsell = document.querySelector('ss-cart-upsell');
      if (upsell) sections.push(upsell.dataset.sectionId);
      return { descriptors, sections: [...new Set(sections)].slice(0, 5) };
    }

    renderSettledCart(cart, descriptors) {
      for (const descriptor of descriptors) {
        const html = cart.sections?.[descriptor.section || descriptor.id];
        if (!html) continue;
        const copy = new DOMParser().parseFromString(html, 'text/html');
        const next = copy.querySelector(descriptor.selector || '.shopify-section');
        const container = document.getElementById(descriptor.id);
        const current = container?.querySelector(descriptor.selector) || container;
        if (current && next) current.innerHTML = next.innerHTML;
      }
      for (const id of ['cart-icon-bubble', 'cart-notification-button']) {
        const html = cart.sections?.[id];
        const current = document.getElementById(id);
        const next = html && new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (current && next) current.innerHTML = next.innerHTML;
      }
      document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach((element) =>
        element.classList.toggle('is-empty', cart.item_count === 0));
      const status = document.getElementById('ss-cart-guides-status');
      if (status) status.textContent = this.state.updatedMessage;
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', ssCartGuides: true, cartData: cart });
      }
    }

    sync() {
      return this.enqueue(async () => {
        try {
          const { descriptors, sections } = this.visibleSections();
          const result = await this.reconcile({ sections });
          if (result) this.renderSettledCart(result, descriptors);
          return result;
        } catch {
          this.reportError();
          return null;
        }
      });
    }

    onCheckout(event) {
      const form = event.target;
      if (this.checkoutForms.has(form)) {
        this.checkoutForms.delete(form);
        return;
      }
      const submitter = event.submitter;
      if (submitter?.name !== 'checkout' && form.id !== 'cart-notification-form') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.sync().finally(() => {
        this.checkoutForms.add(form);
        if (submitter?.isConnected) form.requestSubmit(submitter);
        else if (form.isConnected) {
          const checkout = document.createElement('input');
          checkout.type = 'hidden';
          checkout.name = 'checkout';
          checkout.value = 'checkout';
          form.append(checkout);
          HTMLFormElement.prototype.submit.call(form);
        }
      });
    }

    onCheckoutLink(event) {
      const link = event.target.closest?.('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey ||
          event.shiftKey || event.altKey || link.target === '_blank') return;
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/.test(url.pathname)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.sync().finally(() => window.location.assign(link.href));
    }
  }

  const configuration = document.querySelector('[data-ss-cart-guides]');
  if (configuration) {
    try {
      window.SSCartGuides = new CartGuides(JSON.parse(configuration.textContent));
    } catch { /* The normal storefront remains usable if configuration is unavailable. */ }
  }
  // Exposed only for the local test harness; unused by the storefront.
  if (typeof module !== 'undefined' && module.exports) module.exports = { planChanges, CartGuides };
})();
